import { TrashIcon } from '@phosphor-icons/react'
import { Clock, Plus, Xmark } from 'iconoir-react'
import { keys } from 'lodash'
import { useEffect, useRef } from 'react'
import type { OpenHours } from 'app/types/entities'
import { defaultOpenHours, newSlotFromSeed, type DayKey } from 'app/lib/officeHours'
import { Checkbox } from 'app/components/ui/checkbox'

/**
 * Uncontrolled time input. Browser fully owns DOM value during typing so
 * React re-renders (e.g. FloatingSave appearing on first dirty keystroke)
 * cannot reset the segment mid-edit. Parent state is synced only on valid
 * changes; external value updates (add/remove slot, location switch) push
 * to the DOM via ref.
 */
export function TimeInput({
  value,
  onCommit,
  className,
}: {
  value: string
  onCommit: (newValue: string) => void
  className?: string
}) {
  const ref = useRef<HTMLInputElement>(null)
  const lastSyncedValue = useRef(value)

  useEffect(() => {
    if (ref.current && value !== lastSyncedValue.current && value !== ref.current.value) {
      ref.current.value = value
    }
    lastSyncedValue.current = value
  }, [value])

  return (
    <input
      ref={ref}
      type="time"
      className={className}
      defaultValue={value}
      onChange={(e) => {
        const next = e.target.value
        if (!next || next === lastSyncedValue.current) return
        lastSyncedValue.current = next
        onCommit(next)
      }}
    />
  )
}

const DAY_ORDER: DayKey[] = [`sunday`, `monday`, `tuesday`, `wednesday`, `thursday`, `friday`, `saturday`]

const DAY_LABELS: Record<DayKey, { short: string; full: string }> = {
  sunday: { short: `SUN`, full: `SUNDAY` },
  monday: { short: `MON`, full: `MONDAY` },
  tuesday: { short: `TUE`, full: `TUESDAY` },
  wednesday: { short: `WED`, full: `WEDNESDAY` },
  thursday: { short: `THU`, full: `THURSDAY` },
  friday: { short: `FRI`, full: `FRIDAY` },
  saturday: { short: `SAT`, full: `SATURDAY` },
}

type OfficeHoursEditorProps = {
  /** Current office hours value */
  value: OpenHours | undefined
  /** Form field name for hidden input (serializes value as JSON) */
  name?: string
  /** 'panel' wraps content in a styled panel with header; 'inline' renders just the content */
  variant?: `panel` | `inline`
  /** Panel header title (only used when variant='panel') */
  title?: string
  /**
   * Controls the visual density and style of the editor:
   *
   * compact=true (default) - AI Agent Settings style:
   *   - Short day labels: "MON", "TUE", etc.
   *   - Fixed-width time inputs
   *   - Plus icon aligned to far right
   *   - Xmark icon for removing time slots
   *
   * compact=false - Location Settings style:
   *   - Full day labels: "MONDAY", "TUESDAY", etc.
   *   - Time inputs expand to fill available width
   *   - Plus icon next to time inputs
   *   - Trash icon for removing time slots
   */
  compact?: boolean
  /**
   * Per-day hours used to seed a newly added time slot (the "+" button). When a
   * day has a range here, the new slot copies it instead of the 10:00–18:00
   * default — used by the availability modal to seed new slots from the
   * location's business hours.
   */
  seedHours?: OpenHours
  onChange: (hours: OpenHours) => void
}
/**
 * OfficeHoursEditor - A shared component for editing weekly office hours.
 *
 * Used in:
 * - AI Agent Settings (`/settings/ai-agent/$locationId/settings`) - compact mode
 * - Location Settings General tab (`/settings/location-settings/$locationId/general`) - expanded mode
 *
 * @example
 * // AI Agent settings (compact mode - default)
 * <OfficeHoursEditor
 *   value={businessHours}
 *   onChange={setBusinessHours}
 *   name="businessHours"
 *   variant="panel"
 * />
 *
 * @example
 * // Location Settings (expanded mode)
 * <OfficeHoursEditor
 *   value={openHours}
 *   onChange={setOpenHours}
 *   name="openHours"
 *   variant="inline"
 *   compact={false}
 * />
 */
export default function OfficeHoursEditor({
  value: valueProp,
  name,
  variant = `panel`,
  title = `Office Hours`,
  compact = true,
  seedHours,
  onChange,
}: OfficeHoursEditorProps) {
  const value = valueProp ?? defaultOpenHours
  const orderedDays = DAY_ORDER.filter((day) => keys(value).includes(day))

  const handleToggleDay = (day: DayKey, open: boolean) => {
    onChange({
      ...value,
      [day]: { ...value[day], open },
    })
  }

  const handleUpdateHours = (day: DayKey, index: number, field: `from` | `to`, newValue: string) => {
    if (!newValue) return
    const dayHours = value[day]?.hours ?? []
    if (dayHours[index]?.[field] === newValue) return
    onChange({
      ...value,
      [day]: {
        ...value[day],
        hours: dayHours.map((hour, i) => (i === index ? { ...hour, [field]: newValue } : hour)),
      },
    })
  }

  const handleAddSlot = (day: DayKey) => {
    const dayHours = value[day]?.hours ?? []
    onChange({
      ...value,
      [day]: {
        ...value[day],
        hours: [...dayHours, newSlotFromSeed(seedHours, day)],
      },
    })
  }

  const handleRemoveSlot = (day: DayKey, index: number) => {
    const dayHours = value[day]?.hours ?? []
    onChange({
      ...value,
      [day]: {
        ...value[day],
        hours: dayHours.filter((_, i) => i !== index),
      },
    })
  }

  const dayLabelWidth = compact ? `w-[70px]` : `w-[100px]`
  const timeInputClass = compact
    ? `w-36 rounded-lg border border-strong bg-surface px-2 py-1`
    : `grow rounded-lg border border-strong bg-surface px-2 py-1`

  const content = (
    <div className="flex flex-col gap-4">
      {name && <input type="hidden" name={name} value={JSON.stringify(value)} />}
      {orderedDays.map((day) => (
        <div key={day} className="ml-0.5 flex items-start">
          <Checkbox
            className="mt-3"
            checked={value[day]?.open ?? false}
            onCheckedChange={(checked) => handleToggleDay(day, checked === true)}
          />
          <p className={`ml-2 mt-[14px] shrink-0 text-ui-xs uppercase ${dayLabelWidth}`}>
            {compact ? DAY_LABELS[day].short : DAY_LABELS[day].full}
          </p>
          <div className="ml-3 flex grow flex-col gap-2">
            {(value[day]?.hours ?? []).map((hour, index) => (
              <div key={index} className="flex items-center gap-2">
                <TimeInput
                  className={timeInputClass}
                  value={hour.from}
                  onCommit={(v) => handleUpdateHours(day, index, `from`, v)}
                />
                <span>-</span>
                <TimeInput
                  className={timeInputClass}
                  value={hour.to}
                  onCommit={(v) => handleUpdateHours(day, index, `to`, v)}
                />
                {compact && index === 0 && <div className="grow" />}
                {index === 0 ? (
                  <Plus className="size-5 shrink-0 cursor-pointer" onClick={() => handleAddSlot(day)} />
                ) : compact ? (
                  <Xmark className="size-5 shrink-0 cursor-pointer" onClick={() => handleRemoveSlot(day, index)} />
                ) : (
                  <TrashIcon
                    className="size-5 shrink-0 cursor-pointer text-default"
                    onClick={() => handleRemoveSlot(day, index)}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )

  if (variant === `inline`) {
    return content
  }

  return (
    <div>
      <div className="flex items-center gap-2 rounded-b-none rounded-t-lg border border-default bg-surface-subtle px-4 py-3">
        <Clock height={20} />
        <p className="text-heading-xs text-strong">{title}</p>
      </div>
      <div className="flex flex-col gap-4 rounded-lg !rounded-t-none border !border-t-0 border-default px-6 py-4">
        {content}
      </div>
    </div>
  )
}
